import { Component, Prop, State, Method, h, Element, Build } from '@stencil/core';
import { getConstant } from '../../../../utils/constants-helpers';

const COMPONENT_NAME = 'cookie-type-1';

// Constants
const MAX_LINES = 2;
const LINE_HEIGHT_TOLERANCE = 2.1;
const HEIGHT_TOLERANCE = 2;
const DEFAULT_LINE_HEIGHT = 24;
const LINE_HEIGHT_MULTIPLIER = 1.5;
const RETRY_DELAY = 50;
const POSITIONING_DELAY = 10;
const CHECK_OVERFLOW_DELAY = 100;
const OPEN_PROGRAMMATICALLY_DELAY = 150;

// Cookie names
const COOKIE_REJECTED = 'COOKIES_REJECTED_UPDATED';
const COOKIE_AGREEMENT = 'COOKIES_AGREEMENT_UPDATED';

// Cookie helper functions
const setCookie = (name: string, value: string, options: { domain?: string; path?: string; expires?: number } = {}) => {
  let cookieString = `${name}=${value}`;

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  } else {
    cookieString += `; path=/`;
  }

  // Set expiration to 1 year if not specified
  if (options.expires) {
    const date = new Date();
    date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  } else {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year
    cookieString += `; expires=${date.toUTCString()}`;
  }

  document.cookie = cookieString;
};

/**
 * Gets a cookie value by name
 */
const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }

  return null;
};

/**
 * Checks if user has already interacted with the cookie banner
 */
const hasUserInteracted = (): boolean => {
  return getCookie(COOKIE_REJECTED) !== null || getCookie(COOKIE_AGREEMENT) !== null;
};

/**
 * Cookie banner component with cookie consent functionality
 *
 * @constant COOKIE_BANNER_DESCRIPTION - "We use cookies to improve security, personalize the user experience, enhance our marketing activities (including cooperating with our 3rd party partners) and for other business use. Click <a href=\"/cookies\" title=\"Cookie Policy\">here</a> to&nbsp;read our Cookie Policy. By&nbsp;clicking \"Accept\" you agree to&nbsp;the use of&nbsp;cookies." - HTML content for cookie description (supports links)
 * @constant COOKIE_BANNER_ACCEPT - Accept - Text for Accept button
 * @constant COOKIE_BANNER_READ_MORE - "Read more" - Text for "Read more" link
 * @constant COOKIE_BANNER_READ_LESS - "Read less" - Text for "Read less" link
 */
@Component({
  tag: 'cookie-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class CookieType1 {
  @Element() el: HTMLElement;

  /**
   * Component constants object containing all required text constants
   */
  @Prop() constants?: Record<string, string>;

  /**
   * Delay before showing the cookie popup in milliseconds
   * @default 4000
   */
  @Prop() delay: number = 4000;

  /**
   * Label for close button (for accessibility)
   */
  @Prop() closeLabel: string = 'Close cookie popup';

  @State() isVisible: boolean = false;
  @State() isExpanded: boolean = false;
  @State() needsReadMore: boolean = false; // Will be determined by checkTextOverflow

  private timer: number;
  private slotContainer!: HTMLElement;
  private descriptionRef!: HTMLElement;
  private checkTextOverflowInProgress: boolean = false; // Prevent infinite loops

  /**
   * Gets description text from constants
   */
  private getDescription(): string {
    return getConstant(
      this.constants,
      'COOKIE_BANNER_DESCRIPTION',
      'We use cookies to improve security, personalize the user experience, enhance our marketing activities (including cooperating with our 3rd party partners) and for other business use. Click <a href="/cookies" title="Cookie Policy">here</a> to&nbsp;read our Cookie Policy. By&nbsp;clicking "Accept" you agree to&nbsp;the use of&nbsp;cookies.',
    );
  }

  /**
   * Gets accept button text from constants
   */
  private getAcceptText(): string {
    return getConstant(this.constants, 'COOKIE_BANNER_ACCEPT', 'Accept');
  }

  /**
   * Gets "Read more" text from constants
   */
  private getReadMoreText(): string {
    return getConstant(this.constants, 'COOKIE_BANNER_READ_MORE', 'Read more');
  }

  /**
   * Gets "Read less" text from constants
   */
  private getReadLessText(): string {
    return getConstant(this.constants, 'COOKIE_BANNER_READ_LESS', 'Read less');
  }

  /**
   * Finds the inner content element (p, div, or span) within the description ref
   */
  private getInnerElement(): HTMLElement | null {
    if (!this.descriptionRef) return null;

    // Try to find the main content element (p or div first, not span which might be the read-more link)
    let innerElement = this.descriptionRef.querySelector('p, div') as HTMLElement;

    // If not found, try span as fallback
    if (!innerElement) {
      innerElement = this.descriptionRef.querySelector('span') as HTMLElement;
    }

    return innerElement;
  }

  /**
   * Calculates the effective line height from computed styles
   */
  private calculateLineHeight(computedStyle: CSSStyleDeclaration, fontSize: number): number {
    const lineHeight = parseFloat(computedStyle.lineHeight);

    if (lineHeight && !isNaN(lineHeight) && lineHeight > 0) {
      return lineHeight;
    }

    if (fontSize && !isNaN(fontSize) && fontSize > 0) {
      return fontSize * LINE_HEIGHT_MULTIPLIER;
    }

    return DEFAULT_LINE_HEIGHT;
  }

  /**
   * Updates the description HTML and re-attaches event listeners
   */
  private updateDescriptionHTML(): void {
    if (!this.descriptionRef) return;

    const innerElement = this.getInnerElement();
    if (!innerElement) return;

    const htmlWithLink = this.getDescriptionWithReadMore();
    this.descriptionRef.setAttribute('innerHTML', htmlWithLink);
    innerElement.innerHTML = htmlWithLink;

    // Force reflow
    void innerElement.offsetHeight;
  }

  /**
   * Resets absolute positioning for read-more link (used when expanding)
   */
  private resetReadMoreLinkPosition(): void {
    const innerElement = this.getInnerElement();
    if (!innerElement) return;

    const readMoreInline = innerElement.querySelector(`.${COMPONENT_NAME}__read-more-inline`) as HTMLElement;
    if (readMoreInline) {
      readMoreInline.style.position = 'relative';
      readMoreInline.style.left = 'auto';
      readMoreInline.style.top = 'auto';
      readMoreInline.style.right = 'auto';
      readMoreInline.style.bottom = 'auto';
    }
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;

    // Check if user has already interacted with the banner
    if (hasUserInteracted()) {
      // User has already accepted or rejected cookies, don't show the banner
      return;
    }

    this.timer = window.setTimeout(() => {
      this.isVisible = true;
      // Check if text needs "Read more" after popup becomes visible and styles are applied
      // Use multiple requestAnimationFrame to ensure DOM is fully rendered and styles are applied
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            this.checkTextOverflow();
          }, CHECK_OVERFLOW_DELAY);
        });
      });
    }, this.delay);

    this.slotContainer?.addEventListener('click', this.handleSlotClick);

  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.slotContainer?.removeEventListener('click', this.handleSlotClick);
  }

  private checkTextOverflow = () => {
    if (!Build.isBrowser) return;
    // Prevent infinite loops
    if (this.checkTextOverflowInProgress || !this.descriptionRef || !this.isVisible) {
      return;
    }

    this.checkTextOverflowInProgress = true;

    const innerElement = this.getInnerElement();

    // If inner element not found, the me-text might not be fully rendered yet
    if (!innerElement) {
      this.checkTextOverflowInProgress = false;
      // Retry after a short delay if inner element is not ready
      setTimeout(() => {
        if (!this.checkTextOverflowInProgress) {
          this.checkTextOverflow();
        }
      }, RETRY_DELAY);
      return;
    }

    const wasExpanded = this.isExpanded;

    // Always remove collapsed class first to measure full height
    this.descriptionRef.classList.remove(`${COMPONENT_NAME}__description-text--collapsed`);

    // Temporarily set innerHTML without read more link to measure
    innerElement.innerHTML = this.getDescription();

    // Force reflow to get accurate measurements
    void this.descriptionRef.offsetHeight;
    void innerElement.offsetHeight;

    // Get computed styles and calculate measurements
    const computedStyle = getComputedStyle(innerElement);
    const fullHeight = innerElement.scrollHeight;
    const fontSize = parseFloat(computedStyle.fontSize);
    const effectiveLineHeight = this.calculateLineHeight(computedStyle, fontSize);
    const maxHeight = effectiveLineHeight * MAX_LINES;

    // Store maxHeight for CSS custom property
    this.descriptionRef.style.setProperty('--max-height-2-lines', `${maxHeight}px`);

    // Check if content exceeds MAX_LINES (with tolerance)
    const exceedsTwoLines = fullHeight > maxHeight + HEIGHT_TOLERANCE;

    if (exceedsTwoLines) {
      this.handleTextOverflow(wasExpanded, maxHeight);
    } else {
      this.handleTextFits();
    }
  };

  /**
   * Handles the case when text exceeds the maximum lines
   */
  private handleTextOverflow(wasExpanded: boolean, maxHeight: number): void {
    this.needsReadMore = true;

    if (!wasExpanded) {
      this.descriptionRef.classList.add(`${COMPONENT_NAME}__description-text--collapsed`);
      this.descriptionRef.style.setProperty('--max-height-2-lines', `${maxHeight}px`);
    }

    this.updateDescriptionHTML();

    setTimeout(() => {
      if (!wasExpanded) {
        this.positionReadMoreLink();
      }
      this.attachReadMoreListeners();
      this.checkTextOverflowInProgress = false;
    }, POSITIONING_DELAY);
  }

  /**
   * Handles the case when text fits within the maximum lines
   */
  private handleTextFits(): void {
    this.needsReadMore = false;
    this.descriptionRef.classList.remove(`${COMPONENT_NAME}__description-text--collapsed`);
    this.checkTextOverflowInProgress = false;
  }

  private handleReadMore = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.isExpanded = true;

    if (!this.descriptionRef) return;

    // Remove collapsed class
    this.descriptionRef.classList.remove(`${COMPONENT_NAME}__description-text--collapsed`);

    // Update HTML with read less link
    this.updateDescriptionHTML();

    // Reset positioning for read less link (it should be inline)
    this.resetReadMoreLinkPosition();

    // Re-attach event listeners
    setTimeout(() => {
      this.attachReadMoreListeners();
    }, POSITIONING_DELAY);
  };

  private handleReadLess = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.isExpanded = false;

    if (!this.descriptionRef) return;

    // Add collapsed class
    this.descriptionRef.classList.add(`${COMPONENT_NAME}__description-text--collapsed`);

    // Update HTML with read more link
    this.updateDescriptionHTML();

    // Re-position read more link
    setTimeout(() => {
      this.positionReadMoreLink();
      this.attachReadMoreListeners();
    }, POSITIONING_DELAY);

    // Scroll to top of description
    this.descriptionRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  private positionReadMoreLink = () => {
    if (!this.descriptionRef || !this.needsReadMore || this.isExpanded) return;

    const innerElement = this.descriptionRef.querySelector('p, div') as HTMLElement;
    if (!innerElement) return;

    const readMoreInline = innerElement.querySelector(`.${COMPONENT_NAME}__read-more-inline`) as HTMLElement;
    if (!readMoreInline) return;

    // Get computed styles
    const computedStyle = getComputedStyle(innerElement);
    const fontSize = parseFloat(computedStyle.fontSize);
    const lineHeight = this.calculateLineHeight(computedStyle, fontSize);

    // Temporarily hide read-more link to measure text accurately
    readMoreInline.style.display = 'none';

    // Use Range API to find the end of the second line
    // We'll iterate through text nodes and measure where the second line ends
    if (!Build.isBrowser) return;
    const range = document.createRange();
    const walker = document.createTreeWalker(innerElement, NodeFilter.SHOW_TEXT, null);

    let found = false;
    let lastValidEnd = 0;
    let lastValidNode: Node | null = null;

    // Find where the second line ends by measuring height
    let textNode = walker.nextNode();
    while (textNode) {
      const nodeText = textNode.textContent || '';

      for (let i = 0; i <= nodeText.length; i++) {
        range.setStart(textNode, 0);
        range.setEnd(textNode, i);

        const rect = range.getBoundingClientRect();
        const containerRect = innerElement.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;

        // If we're still within MAX_LINES, save this position
        if (relativeTop <= lineHeight * LINE_HEIGHT_TOLERANCE) {
          lastValidEnd = i;
          lastValidNode = textNode;
        } else {
          // We've exceeded 2 lines, use the last valid position
          if (lastValidNode) {
            range.setStart(lastValidNode, 0);
            range.setEnd(lastValidNode, lastValidEnd);
            found = true;
          }
          break;
        }
      }

      if (found) break;
      textNode = walker.nextNode();
    }

    // If we didn't find a break point, use the last valid position
    if (!found && lastValidNode) {
      range.setStart(lastValidNode, 0);
      range.setEnd(lastValidNode, lastValidEnd);
      found = true;
    }

    // Restore read-more link visibility
    readMoreInline.style.display = '';

    if (found) {
      const rect = range.getBoundingClientRect();
      const containerRect = innerElement.getBoundingClientRect();

      // Position link at the end of the second line
      // The ellipsis before the link will be positioned right after the text
      const leftPosition = rect.right - containerRect.left;
      const topPosition = lineHeight; // Second line starts at lineHeight (first line is 0, second is lineHeight)

      readMoreInline.style.left = `${leftPosition}px`;
      readMoreInline.style.top = `${topPosition}px`;
    } else {
      // Fallback: position at bottom right
      readMoreInline.style.right = '0';
      readMoreInline.style.bottom = '0';
    }
  };

  private attachReadMoreListeners = () => {
    if (!this.descriptionRef) return;

    // Get the inner element rendered by me-text (p, span, or div)
    const innerElement = this.descriptionRef.querySelector('p, span, div') || this.descriptionRef;

    const readMoreLink = innerElement.querySelector(`.${COMPONENT_NAME}__read-more-link`);
    if (readMoreLink) {
      // Remove existing listeners by cloning
      const newLink = readMoreLink.cloneNode(true);
      readMoreLink.parentNode?.replaceChild(newLink, readMoreLink);

      newLink.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        if (this.isExpanded) {
          this.handleReadLess(e as MouseEvent);
        } else {
          this.handleReadMore(e as MouseEvent);
        }
      });
      newLink.addEventListener('keydown', e => {
        const keyEvent = e as KeyboardEvent;
        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
          keyEvent.preventDefault();
          if (this.isExpanded) {
            this.handleReadLess(e as any);
          } else {
            this.handleReadMore(e as any);
          }
        }
      });
    }
  };

  private getDescriptionWithReadMore = (): string => {
    const descriptionHtml = this.getDescription();

    if (!this.needsReadMore) {
      return descriptionHtml;
    }

    // If expanded, show full text with "Read less" link (no ellipsis)
    if (this.isExpanded) {
      const readLessLink = `<span class="${COMPONENT_NAME}__read-more-inline"> <a href="#" class="${COMPONENT_NAME}__read-more-link">${this.getReadLessText()}</a></span>`;
      return `${descriptionHtml}${readLessLink}`;
    }

    // If collapsed, add "Read more" link with ellipsis before and after
    // Ellipsis before link (no space) - will be positioned absolutely at end of second line
    // Ellipsis after "Read more" text
    const readMoreLink = `<span class="${COMPONENT_NAME}__read-more-inline"><span class="${COMPONENT_NAME}__ellipsis-before">...</span><a href="#" class="${COMPONENT_NAME}__read-more-link">${this.getReadMoreText()}</a>...</span>`;
    return `${descriptionHtml}${readMoreLink}`;
  };

  private closePopup = () => {
    this.isVisible = false;
  };

  private handleClose = () => {
    // Set cookie when closing via close button (rejecting cookies)
    setCookie(COOKIE_REJECTED, 'true', { path: '/' });
    this.closePopup();
  };

  private handleCloseKeyDown = (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      this.handleClose();
    }
  };

  private handleSlotClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const button = target.closest('me-button');

    if (button) {
      // Check if this is the Accept button (has slot="action-primary" attribute)
      const slot = button.getAttribute('slot');
      if (slot === 'action-primary') {
        // Set cookie when accepting cookies
        setCookie(COOKIE_AGREEMENT, 'true', { path: '/' });
      }
      // Close popup without setting rejection cookie
      this.closePopup();
    }
  };

  /**
   * Public method to open the cookie popup programmatically
   */
  @Method()
  async open(): Promise<void> {
    // Check if user has already interacted with the banner
    if (hasUserInteracted()) {
      // User has already accepted or rejected cookies, don't show the banner
      return;
    }

    this.isVisible = true;
    // Check overflow after opening programmatically
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => this.checkTextOverflow(), OPEN_PROGRAMMATICALLY_DELAY);
      });
    });
  }

  render() {
    const acceptText = this.getAcceptText();
    const collapsedClass = !this.isExpanded && this.needsReadMore ? `${COMPONENT_NAME}__description-text--collapsed` : '';
    const descriptionHtml = this.needsReadMore ? this.getDescriptionWithReadMore() : this.getDescription();

    return (
      <div ref={el => (this.slotContainer = el)} role="dialog" aria-modal="true" aria-hidden={!this.isVisible} class={{ [COMPONENT_NAME]: true, 'is-visible': this.isVisible }}>
        <div class={`${COMPONENT_NAME}__content`}>
          <div class={`${COMPONENT_NAME}__description`}>
            <div class={`${COMPONENT_NAME}__description-wrapper`}>
              <me-text
                ref={el => {
                  if (el) {
                    this.descriptionRef = el;
                    // Apply collapsed class immediately to prevent showing full text
                    if (!this.isExpanded && this.needsReadMore) {
                      this.descriptionRef.classList.add(`${COMPONENT_NAME}__description-text--collapsed`);
                    }
                    // Don't call checkTextOverflow from ref callback - it's already called from componentDidLoad
                  }
                }}
                class={`${COMPONENT_NAME}__description-text ${collapsedClass}`}
                innerHTML={descriptionHtml}
              ></me-text>
            </div>
          </div>
          <div class={`${COMPONENT_NAME}__actions`}>
            <me-button slot="action-primary" variant="secondary">
              {acceptText}
            </me-button>
          </div>
        </div>
        <div class={`${COMPONENT_NAME}__close`}>
          <me-button
            iconPosition="only"
            icon="interface--x"
            variant="ghost"
            aria-label={this.closeLabel}
            onClick={this.handleClose}
            onKeyDown={this.handleCloseKeyDown}
          ></me-button>
        </div>
      </div>
    );
  }
}
